import { List } from '@kerfjs/ui/list';
import { ListItem } from '@kerfjs/ui/list-item';
import { ValueTable, ValueTableRow } from '@kerfjs/ui/value-table';
import { type SafeHtml } from 'kerfjs';

import { siteDialog } from '../../data/state.js';
import { isOrgAdmin } from '../../lib/context.js';
import { SiteDialog } from './site-dialog.js';

export interface SiteRow {
  id: string;
  name: string;
  location?: string | null;
  isDefault?: boolean;
}

function siteDetail(site: SiteRow): string {
  return [site.location ?? '', site.isDefault === true ? 'default' : ''].filter(Boolean).join(' · ');
}

/**
 * The organization's sites. Admins get one row per site that opens the Sites
 * dialog to edit it (added from the header's "New Site…", HS2-DE4RP1); other
 * staff see a read-only table.
 */
export function SitesView({
  sites,
  canManage = isOrgAdmin(),
}: {
  sites: readonly SiteRow[];
  canManage?: boolean;
}): SafeHtml {
  const dialog = siteDialog.value;
  return (
    <List controlInsets="tb" className="sites-view sites-page">
      {canManage ? (
        <List rootAttributes={{ 'data-sites-list': '' }}>
          {/* Dividers only between rows, so Kerf rounds the first row's top and the last row's
              bottom; a lone row stays fully rounded (HS2-1CPAMR, KF-AQN4H7). */}
          {sites.map((site, index) => (
            <ListItem
              label={site.name}
              description={site.location ?? undefined}
              status={site.isDefault === true ? 'Default' : undefined}
              action="site-edit"
              itemId={site.id}
              accessibleLabel={`Edit ${site.name}`}
              divider={index < sites.length - 1 ? 'after' : 'none'}
            />
          ))}
        </List>
      ) : (
        <ValueTable label="Organization Sites">
          {sites.map((site) => (
            <ValueTableRow label={site.name} value={siteDetail(site) || '—'} />
          ))}
        </ValueTable>
      )}
      {canManage && dialog !== null ? (
        <SiteDialog
          site={dialog.siteId === null ? undefined : sites.find((site) => site.id === dialog.siteId)}
          error={dialog.error}
        />
      ) : null}
    </List>
  );
}
